import React, { useState } from "react";
import TalkingAvatar from "./TalkingAvatar";
import { Message, ThemeConfig, getThemeValue } from "../hooks/useVoiceWidget";
import { getStrings, Language } from "../i18n/strings";
import { NEUTRAL_PALETTE } from "../utils/neutralPalette";
import { HeyAiCopyIcon, HeyAiCheckIcon } from "./HeyAiIcons";

const URL_REGEX = /(https?:\/\/[^\s<>"']+)/g;

function linkifyText(text: string): React.ReactNode {
  const parts = text.split(URL_REGEX);
  return parts.map((part, i) =>
    URL_REGEX.test(part) ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "currentColor", textDecoration: "underline" }}
      >
        {part}
      </a>
    ) : (
      part
    )
  );
}

interface MessageBubbleProps {
  message: Message;
  isLastAgentMessage: boolean;
  isAgentSpeaking: boolean;
  avatarUrl?: string;
  theme?: Partial<ThemeConfig>;
  uiVariant?: "legacy" | "split-switch";
  language?: Language;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  isLastAgentMessage,
  isAgentSpeaking,
  avatarUrl,
  theme,
  uiVariant = "legacy",
  language,
}) => {
  const t = getStrings(language);
  const isUser = message.author === "user";
  const useNeutralTextWindow = uiVariant === "split-switch";
  const userBackground = useNeutralTextWindow
    ? NEUTRAL_PALETTE.anthracite
    : getThemeValue(theme, "userMessageBackground");
  const agentBackground = useNeutralTextWindow
    ? NEUTRAL_PALETTE.pearl
    : getThemeValue(theme, "agentMessageBackground");
  const userColor = useNeutralTextWindow
    ? "#ffffff"
    : getThemeValue(theme, "userMessageColor");
  const agentColor = useNeutralTextWindow
    ? NEUTRAL_PALETTE.anthracite
    : getThemeValue(theme, "agentMessageColor");

  const [copied, setCopied] = useState(false);
  const copyLabel = language === "en" ? "Copy" : "Kopieren";
  const copiedLabel = language === "en" ? "Copied" : "Kopiert";

  const handleCopy = () => {
    const showCopied = () => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    };
    // execCommand fallback: the async Clipboard API is permission-gated and
    // rejects in some embeds (cross-origin iframes, denied permission), so fall
    // back to the legacy path which works inside a click gesture.
    const fallbackCopy = () => {
      try {
        const ta = document.createElement("textarea");
        ta.value = message.text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (ok) showCopied();
      } catch {
        /* clipboard unavailable — leave the button label unchanged */
      }
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(message.text).then(showCopied).catch(fallbackCopy);
    } else {
      fallbackCopy();
    }
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: isUser ? "flex-end" : "flex-start",
        alignItems: "flex-end",
        gap: "8px",
      }}
    >
      {!isUser && !useNeutralTextWindow && (
        <TalkingAvatar
          isRecording={false}
          isSpeaking={isAgentSpeaking && isLastAgentMessage}
          size={32}
          avatarUrl={avatarUrl}
          tone={useNeutralTextWindow ? "neutral" : "default"}
        />
      )}
      <div
        style={{
          maxWidth: useNeutralTextWindow ? "78%" : "70%",
          padding: "10px 14px",
          borderRadius: isUser ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
          backgroundColor: isUser ? userBackground : agentBackground,
          color: isUser ? userColor : agentColor,
          fontSize: getThemeValue(theme, "fontSize"),
          lineHeight: "1.45",
          wordWrap: "break-word",
          border:
            useNeutralTextWindow && !isUser
              ? `1px solid ${NEUTRAL_PALETTE.mist}`
              : "none",
          boxShadow: useNeutralTextWindow
            ? "0 1px 2px rgba(94, 93, 89, 0.06)"
            : "0 1px 2px rgba(0, 0, 0, 0.05)",
        }}
      >
        <div
          data-testid="message-text"
          style={{
            // Preserve the paragraph breaks the agent already sends, and let
            // the user select/copy the text. The widget root sets
            // user-select:none; this inline value overrides it for message
            // text only. Voice-safe: rendering only, no agent-wording change.
            whiteSpace: "pre-wrap",
            wordWrap: "break-word",
            userSelect: "text",
            WebkitUserSelect: "text",
          }}
        >
          {linkifyText(message.text)}
        </div>
        {!isUser && (
          <button
            type="button"
            data-testid="message-copy-button"
            onClick={handleCopy}
            aria-label={copied ? copiedLabel : copyLabel}
            title={copied ? copiedLabel : copyLabel}
            style={{
              marginTop: "6px",
              padding: 0,
              border: 0,
              background: "transparent",
              cursor: "pointer",
              lineHeight: 0,
              display: "inline-flex",
              alignItems: "center",
              color: useNeutralTextWindow ? NEUTRAL_PALETTE.slate : "#94a3b8",
              userSelect: "none",
              WebkitUserSelect: "none",
            }}
          >
            {copied ? <HeyAiCheckIcon size={15} /> : <HeyAiCopyIcon size={15} />}
          </button>
        )}
      </div>
      {isUser && (
        <div
          style={{
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            backgroundColor: useNeutralTextWindow ? NEUTRAL_PALETTE.anthracite : "#3b82f6",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            fontSize: "12px",
            fontWeight: "600",
          }}
        >
          {t.userLabel}
        </div>
      )}
    </div>
  );
};

export default MessageBubble;
